import { createDefaultSessionContext, SessionContext } from "../types/sessionContext";

export type StepFieldConfig = {
    fieldName: string;
    fieldType:
        | "text"
        | "date"
        | "select"
        | "checkbox"
        | "number"
        | "email"
        | "tel"
        | "password"
        | "textarea"
        | "radio"
        | "file"
        | "toggle"
        | "multiselect"
        | "range"
        | "color"
        | "time"
        | "datetime-local"
        | "url"
        | "search"
        | "hidden"
        | "table";
    required: boolean;
    label: string;
    labelKey: string;
    // Translation keys
    placeholderKey?: string;
    descriptionKey?: string;
    helpTextKey?: string;
    errorMessageKey?: string;
    successMessageKey?: string;
    // For select/radio/multiselect fields
    optionsKeys?: Record<string, string>;

    // Field configuration
    defaultValue?: string | string[] | number | boolean | any[] | Record<string, any>;
    dependsOn?: {
        field: string;
        value: string | string[] | number | boolean;
        condition?: "equals" | "notEquals" | "contains" | "notContains" | "greater" | "less";
    };

    // Validation and constraints
    validationRules?: {
        pattern?: string;
        patternErrorKey?: string;
        maxLength?: number;
        minLength?: number;
        min?: number;
        max?: number;
        step?: number;
    };

    // Field appearance/behavior
    readOnly?: boolean;
    disabled?: boolean;
    autoComplete?: string;
    className?: string;

    // Field-type specific
    rows?: number; // For textarea
    cols?: number; // For textarea
    accept?: string; // For file (e.g., "image/*")
    multiple?: boolean; // For file/multiselect

    // Visual and interaction hints
    iconStart?: string; // Icon name for icon before input
    iconEnd?: string; // Icon name for icon after input
    hintKey?: string; // Additional hint text below the input
    tooltipKey?: string; // Tooltip text

    // Table-specific configuration
    tableConfig?: {
        columns: {
            key: string;
            headerKey: string; // Translation key for header
            width?: string; // CSS width value (e.g., "20%", "200px")
            align?: "left" | "center" | "right";
            cellType?: "text" | "number" | "date" | "status";
            statusConfig?: {
                options: {
                    value: string;
                    labelKey: string;
                    color: string;
                }[];
            };
        }[];
        rowActions?: {
            icon: string;
            labelKey: string;
            action: string;
        }[];
        defaultSort?: {
            column: string;
            direction: "asc" | "desc";
        };
    };
};

export type StepConfig = {
    id: number;
    key: string;
    promptStep: string; // Maps to "un_1", "un_2", etc. format in your existing update_prompt endpoint
    screen: string;
    titleKey: string;
    fields: StepFieldConfig[];
    hasChat: boolean;
    LLM_data?: boolean; // New flag
    llmDataTargetField?: string; // New: specify which field to populate
};

export type DemoSiteConfig = {
    route: string;
    name: string;
    steps: StepConfig[];
    defaultAssistant: {
        tab: "chat" | "call";
        language: string;
        lastPrompt: string;
        lastToolUsed: string;
    };
};

// Define BASE Demo site configuration
export const BASE_DEMO_CONFIG: DemoSiteConfig = {
    route: "/basedemo",
    name: "baseDemo",
    steps: [
        {
            id: 1,
            key: "Step1",
            promptStep: "base_1",
            screen: "Step1Form",
            titleKey: "base_demo.workflow.steps.step1form.title",
            fields: [], // Define fields as needed
            hasChat: true
        },
        {
            id: 2,
            key: "Step2",
            promptStep: "base_2",
            screen: "Step2Form",
            titleKey: "base_demo.workflow.steps.step2form.title",
            fields: [
                {
                    fieldName: "email",
                    fieldType: "email",
                    required: true,
                    label: "Email Address",
                    labelKey: "base_demo.step2form.email.label",
                    placeholderKey: "base_demo.step2form.email.placeholder",
                    validationRules: {
                        pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
                        patternErrorKey: "base_demo.step2form.email.patternError"
                    },
                    autoComplete: "email"
                },
                {
                    fieldName: "phoneNumber",
                    fieldType: "tel",
                    required: true,
                    label: "Phone Number",
                    labelKey: "base_demo.step2form.phoneNumber.label",
                    placeholderKey: "base_demo.step2form.phoneNumber.placeholder",
                    helpTextKey: "base_demo.step2form.phoneNumber.help",
                    validationRules: {
                        pattern: "^\\+?[0-9\\s-]{10,15}$",
                        patternErrorKey: "base_demo.step2form.phoneNumber.patternError"
                    },
                    autoComplete: "tel"
                },
                {
                    fieldName: "comments",
                    fieldType: "textarea",
                    required: false,
                    label: "Additional Comments",
                    labelKey: "base_demo.step2form.comments.label",
                    placeholderKey: "base_demo.step2form.comments.placeholder",
                    rows: 4,
                    validationRules: {
                        maxLength: 500
                    }
                },
                {
                    fieldName: "preferredContact",
                    fieldType: "radio",
                    required: true,
                    label: "Preferred Contact Method",
                    labelKey: "base_demo.step2form.preferredContact.label",
                    optionsKeys: {
                        email: "base_demo.step2form.preferredContact.options.email",
                        phone: "base_demo.step2form.preferredContact.options.phone",
                        sms: "base_demo.step2form.preferredContact.options.sms"
                    },
                    defaultValue: "email"
                },
                {
                    fieldName: "supportingDocuments",
                    fieldType: "file",
                    required: false,
                    label: "Supporting Documents",
                    labelKey: "base_demo.step2form.documents.label",
                    descriptionKey: "base_demo.step2form.documents.description",
                    accept: "application/pdf,image/jpeg,image/png",
                    multiple: true
                },
                {
                    fieldName: "receiveUpdates",
                    fieldType: "toggle",
                    required: false,
                    label: "Receive Status Updates",
                    labelKey: "base_demo.step2form.receiveUpdates.label",
                    defaultValue: true
                },
                {
                    fieldName: "scheduleTime",
                    fieldType: "time",
                    required: false,
                    label: "Preferred Contact Time",
                    labelKey: "base_demo.step2form.scheduleTime.label",
                    dependsOn: {
                        field: "preferredContact",
                        value: "phone",
                        condition: "equals"
                    }
                },
                {
                    fieldName: "identificationColor",
                    fieldType: "color",
                    required: false,
                    label: "Identification Color",
                    labelKey: "base_demo.step2form.identificationColor.label",
                    descriptionKey: "base_demo.step2form.identificationColor.description"
                },
                {
                    fieldName: "satisfactionLevel",
                    fieldType: "range",
                    required: false,
                    label: "Satisfaction Level",
                    labelKey: "base_demo.step2form.satisfactionLevel.label",
                    validationRules: {
                        min: 1,
                        max: 10,
                        step: 1
                    }
                }
            ],
            hasChat: true
        },
        // Add step 3
        {
            id: 3,
            key: "Step3",
            promptStep: "base_3",
            screen: "Step3Form",
            titleKey: "base_demo.workflow.steps.step3form.title",
            fields: [
                {
                    fieldName: "paymentMethod",
                    fieldType: "select",
                    required: true,
                    label: "Payment Method",
                    labelKey: "base_demo.step3form.paymentMethod.label"
                },
                {
                    fieldName: "insuranceInfo",
                    fieldType: "text",
                    required: true,
                    label: "Insurance Information",
                    labelKey: "base_demo.step3form.insuranceInfo.label"
                },
                {
                    fieldName: "odometerReading",
                    fieldType: "text",
                    required: true,
                    label: "Odometer Reading",
                    labelKey: "base_demo.step3form.odometerReading.label"
                },
                {
                    fieldName: "transferDate",
                    fieldType: "date",
                    required: true,
                    label: "Transfer Date",
                    labelKey: "base_demo.step3form.transferDate.label"
                },
                {
                    fieldName: "feeAcknowledged",
                    fieldType: "checkbox",
                    required: true,
                    label: "I acknowledge the transfer fee",
                    labelKey: "base_demo.step3form.feeAcknowledgment"
                }
            ],
            hasChat: true
        },
        // Add step 4
        {
            id: 4,
            key: "Step4",
            promptStep: "base_4",
            screen: "Step4Form",
            titleKey: "base_demo.workflow.steps.step4form.title",
            fields: [
                {
                    fieldName: "confirmationNumber",
                    fieldType: "text",
                    required: false,
                    label: "Confirmation Number",
                    labelKey: "base_demo.step4form.confirmationNumber.label"
                },
                {
                    fieldName: "termsAccepted",
                    fieldType: "checkbox",
                    required: true,
                    label: "Terms and Conditions",
                    labelKey: "base_demo.step4form.termsAndConditions"
                },
                {
                    fieldName: "declared",
                    fieldType: "checkbox",
                    required: true,
                    label: "Declaration",
                    labelKey: "base_demo.step4form.declaration"
                }
            ],
            hasChat: true
        }
    ],
    defaultAssistant: {
        tab: "chat",
        language: "en",
        lastPrompt: "base_1",
        lastToolUsed: ""
    }
};

// Define P2 Demo site configuration
export const P2_DEMO_CONFIG: DemoSiteConfig = {
    route: "/p2demo",
    name: "p2Demo",
    steps: [
        {
            id: 1,
            key: "Step1",
            promptStep: "p2_1",
            screen: "Step1Form",
            titleKey: "p2_demo.workflow.steps.step1form.title",
            fields: [
                {
                    fieldName: "supportingDocuments",
                    fieldType: "file",
                    required: false,
                    label: "RFP-related documents",
                    labelKey: "base_demo.step2form.documents.label",
                    descriptionKey: "base_demo.step2form.documents.description",
                    accept: "application/pdf,image/jpeg,image/png",
                    multiple: true
                }
            ], // Define fields as needed
            hasChat: true
        },
        {
            id: 2,
            key: "Step2",
            promptStep: "p2_2",
            screen: "Step2Form",
            titleKey: "p2_demo.workflow.steps.step2form.title",
            LLM_data: true, // Enable LLM data fetching for this step
            llmDataTargetField: "evaluationTable", // Target this field
            fields: [
                {
                    fieldName: "evaluationTable",
                    fieldType: "table",
                    required: false,
                    label: "Evaluation Criteria",
                    labelKey: "p2_demo.step2form.evaluationTable.label",
                    tableConfig: {
                        columns: [
                            {
                                key: "category",
                                headerKey: "p2_demo.step2form.evaluationTable.category",
                                width: "15%"
                            },
                            {
                                key: "criteria",
                                headerKey: "p2_demo.step2form.evaluationTable.criteria",
                                width: "25%"
                            },
                            {
                                key: "weight",
                                headerKey: "p2_demo.step2form.evaluationTable.weight",
                                width: "10%",
                                align: "center",
                                cellType: "text"
                            },
                            {
                                key: "scoringGuide",
                                headerKey: "p2_demo.step2form.evaluationTable.scoringGuide",
                                width: "25%"
                            },
                            {
                                key: "notes",
                                headerKey: "p2_demo.step2form.evaluationTable.notes",
                                width: "25%"
                            }
                        ],
                        defaultSort: {
                            column: "category",
                            direction: "asc"
                        }
                    },
                    defaultValue: [
                        {
                            category: "Eligibility",
                            criteria: "Not debarred from public sector tenders on or after Closing Date",
                            weight: "Critical",
                            scoringGuide: "Pass/Fail",
                            notes: "Tenderers must not be debarred. Offers that do not meet this criterion will be disqualified."
                        },
                        {
                            category: "Compliance",
                            criteria: "Full compliance with Invitation to Tender and related documents",
                            weight: "Critical",
                            scoringGuide: "Pass/Fail",
                            notes: "Adherence to all tender instructions and requirements is necessary. Non-compliance may lead to disqualification."
                        },
                        {
                            category: "Price Proposal",
                            criteria: "Competitive Pricing with detailed breakdown of costs",
                            weight: "60%",
                            scoringGuide: "Scored based on pricing competitiveness compared to other submissions",
                            notes: "Evaluates price reasonableness and detail of cost breakdown."
                        },
                        {
                            category: "Quality Proposal",
                            criteria: "Draft Implementation Plan for Service Blueprint",
                            weight: "20%",
                            scoringGuide: "Scored on understanding of scope, design thinking competency, feasibility, innovation",
                            notes: "Minimum quality score of 20% required for further evaluation."
                        },
                        {
                            category: "Staffing",
                            criteria: "CVs of personnel assigned to the project",
                            weight: "10%",
                            scoringGuide: "Scored on experience and relevance of assigned team members",
                            notes: "Evaluates the team structure, individual experience, and project-specific expertise."
                        },
                        {
                            category: "Past Performance",
                            criteria: "Performance of relevant completed projects",
                            weight: "10%",
                            scoringGuide: "Scored on relevance, client testimonials, awards, and client performance reports",
                            notes: "Considers past experience and proven capabilities in similar projects."
                        },
                        {
                            category: "Tie Breaker",
                            criteria: "Highest score in Draft Implementation Plan",
                            weight: "-",
                            scoringGuide: "Utilized only in the event of a tie",
                            notes: "Used to differentiate between tied Tenderers based on the strategic importance of the implementation plan."
                        }
                    ]
                }
            ],
            hasChat: true
        },
        // Add step 3
        {
            id: 3,
            key: "Step3",
            promptStep: "p2_3",
            screen: "Step3Form",
            titleKey: "p2_demo.workflow.steps.step3form.title",
            fields: [
                {
                    fieldName: "paymentMethod",
                    fieldType: "select",
                    required: true,
                    label: "Payment Method",
                    labelKey: "p2_demo.step3form.paymentMethod.label"
                },
                {
                    fieldName: "insuranceInfo",
                    fieldType: "text",
                    required: true,
                    label: "Insurance Information",
                    labelKey: "p2_demo.step3form.insuranceInfo.label"
                },
                {
                    fieldName: "odometerReading",
                    fieldType: "text",
                    required: true,
                    label: "Odometer Reading",
                    labelKey: "p2_demo.step3form.odometerReading.label"
                },
                {
                    fieldName: "transferDate",
                    fieldType: "date",
                    required: true,
                    label: "Transfer Date",
                    labelKey: "p2_demo.step3form.transferDate.label"
                },
                {
                    fieldName: "feeAcknowledged",
                    fieldType: "checkbox",
                    required: true,
                    label: "I acknowledge the transfer fee",
                    labelKey: "p2_demo.step3form.feeAcknowledgment"
                }
            ],
            hasChat: true
        },
        // Add step 4
        {
            id: 4,
            key: "Step4",
            promptStep: "p2_4",
            screen: "Step4Form",
            titleKey: "p2_demo.workflow.steps.step4form.title",
            fields: [
                {
                    fieldName: "confirmationNumber",
                    fieldType: "text",
                    required: false,
                    label: "Confirmation Number",
                    labelKey: "p1_demo.step4form.confirmationNumber.label"
                },
                {
                    fieldName: "termsAccepted",
                    fieldType: "checkbox",
                    required: true,
                    label: "Terms and Conditions",
                    labelKey: "p1_demo.step4form.termsAndConditions"
                },
                {
                    fieldName: "declared",
                    fieldType: "checkbox",
                    required: true,
                    label: "Declaration",
                    labelKey: "p1_demo.step4form.declaration"
                }
            ],
            hasChat: true
        }
    ],
    defaultAssistant: {
        tab: "chat",
        language: "en",
        lastPrompt: "p2_1",
        lastToolUsed: ""
    }
};

// Define P1 Demo site configuration
export const P1_DEMO_CONFIG: DemoSiteConfig = {
    route: "/p1demo",
    name: "p1Demo",
    steps: [
        {
            id: 1,
            key: "Step1",
            promptStep: "p1_1",
            screen: "Step1Form",
            titleKey: "p1_demo.workflow.steps.step1form.title",
            fields: [], // Define fields as needed
            hasChat: true
        },
        {
            id: 2,
            key: "Step2",
            promptStep: "p1_2",
            screen: "Step2Form",
            titleKey: "p1_demo.workflow.steps.step2form.title",
            fields: [
                {
                    fieldName: "email",
                    fieldType: "email",
                    required: true,
                    label: "Email Address",
                    labelKey: "p1_demo.step2form.email.label",
                    placeholderKey: "p1_demo.step2form.email.placeholder",
                    validationRules: {
                        pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
                        patternErrorKey: "p1_demo.step2form.email.patternError"
                    },
                    autoComplete: "email"
                },
                {
                    fieldName: "phoneNumber",
                    fieldType: "tel",
                    required: true,
                    label: "Phone Number",
                    labelKey: "p1_demo.step2form.phoneNumber.label",
                    placeholderKey: "p1_demo.step2form.phoneNumber.placeholder",
                    helpTextKey: "p1_demo.step2form.phoneNumber.help",
                    validationRules: {
                        pattern: "^\\+?[0-9\\s-]{10,15}$",
                        patternErrorKey: "p1_demo.step2form.phoneNumber.patternError"
                    },
                    autoComplete: "tel"
                },
                {
                    fieldName: "comments",
                    fieldType: "textarea",
                    required: false,
                    label: "Additional Comments",
                    labelKey: "p1_demo.step2form.comments.label",
                    placeholderKey: "p1_demo.step2form.comments.placeholder",
                    rows: 4,
                    validationRules: {
                        maxLength: 500
                    }
                },
                {
                    fieldName: "preferredContact",
                    fieldType: "radio",
                    required: true,
                    label: "Preferred Contact Method",
                    labelKey: "p1_demo.step2form.preferredContact.label",
                    optionsKeys: {
                        email: "p1_demo.step2form.preferredContact.options.email",
                        phone: "p1_demo.step2form.preferredContact.options.phone",
                        sms: "p1_demo.step2form.preferredContact.options.sms"
                    },
                    defaultValue: "email"
                },
                {
                    fieldName: "supportingDocuments",
                    fieldType: "file",
                    required: false,
                    label: "Supporting Documents",
                    labelKey: "p1_demo.step2form.documents.label",
                    descriptionKey: "p1_demo.step2form.documents.description",
                    accept: "application/pdf,image/jpeg,image/png",
                    multiple: true
                },
                {
                    fieldName: "receiveUpdates",
                    fieldType: "toggle",
                    required: false,
                    label: "Receive Status Updates",
                    labelKey: "p1_demo.step2form.receiveUpdates.label",
                    defaultValue: true
                },
                {
                    fieldName: "scheduleTime",
                    fieldType: "time",
                    required: false,
                    label: "Preferred Contact Time",
                    labelKey: "p1_demo.step2form.scheduleTime.label",
                    dependsOn: {
                        field: "preferredContact",
                        value: "phone",
                        condition: "equals"
                    }
                },
                {
                    fieldName: "identificationColor",
                    fieldType: "color",
                    required: false,
                    label: "Identification Color",
                    labelKey: "p1_demo.step2form.identificationColor.label",
                    descriptionKey: "p1_demo.step2form.identificationColor.description"
                },
                {
                    fieldName: "satisfactionLevel",
                    fieldType: "range",
                    required: false,
                    label: "Satisfaction Level",
                    labelKey: "p1_demo.step2form.satisfactionLevel.label",
                    validationRules: {
                        min: 1,
                        max: 10,
                        step: 1
                    }
                }
            ],
            hasChat: true
        },
        // Add step 3
        {
            id: 3,
            key: "Step3",
            promptStep: "p1_3",
            screen: "Step3Form",
            titleKey: "p1_demo.workflow.steps.step3form.title",
            fields: [
                {
                    fieldName: "paymentMethod",
                    fieldType: "select",
                    required: true,
                    label: "Payment Method",
                    labelKey: "p1_demo.step3form.paymentMethod.label"
                },
                {
                    fieldName: "insuranceInfo",
                    fieldType: "text",
                    required: true,
                    label: "Insurance Information",
                    labelKey: "p1_demo.step3form.insuranceInfo.label"
                },
                {
                    fieldName: "odometerReading",
                    fieldType: "text",
                    required: true,
                    label: "Odometer Reading",
                    labelKey: "p1_demo.step3form.odometerReading.label"
                },
                {
                    fieldName: "transferDate",
                    fieldType: "date",
                    required: true,
                    label: "Transfer Date",
                    labelKey: "p1_demo.step3form.transferDate.label"
                },
                {
                    fieldName: "feeAcknowledged",
                    fieldType: "checkbox",
                    required: true,
                    label: "I acknowledge the transfer fee",
                    labelKey: "p1_demo.step3form.feeAcknowledgment"
                }
            ],
            hasChat: true
        },
        // Add step 4
        {
            id: 4,
            key: "Step4",
            promptStep: "p1_4",
            screen: "Step4Form",
            titleKey: "p1_demo.workflow.steps.step4form.title",
            fields: [
                {
                    fieldName: "confirmationNumber",
                    fieldType: "text",
                    required: false,
                    label: "Confirmation Number",
                    labelKey: "p1_demo.step4form.confirmationNumber.label"
                },
                {
                    fieldName: "termsAccepted",
                    fieldType: "checkbox",
                    required: true,
                    label: "Terms and Conditions",
                    labelKey: "p1_demo.step4form.termsAndConditions"
                },
                {
                    fieldName: "declared",
                    fieldType: "checkbox",
                    required: true,
                    label: "Declaration",
                    labelKey: "p1_demo.step4form.declaration"
                }
            ],
            hasChat: true
        }
    ],
    defaultAssistant: {
        tab: "chat",
        language: "en",
        lastPrompt: "p1_1",
        lastToolUsed: ""
    }
};

// Define P3 Demo site configuration
export const P3_DEMO_CONFIG: DemoSiteConfig = {
    route: "/p3demo",
    name: "p3Demo",
    steps: [
        {
            id: 1,
            key: "Step1",
            promptStep: "p3_1",
            screen: "Step1Form",
            titleKey: "p3_demo.workflow.steps.step1form.title",
            fields: [
                {
                    fieldName: "email",
                    fieldType: "email",
                    required: true,
                    label: "Email Address",
                    labelKey: "p3_demo.step2form.email.label",
                    placeholderKey: "p3_demo.step2form.email.placeholder",
                    validationRules: {
                        pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
                        patternErrorKey: "p3_demo.step2form.email.patternError"
                    },
                    autoComplete: "email"
                }
            ], // Define fields as needed
            hasChat: true
        },
        {
            id: 2,
            key: "Step2",
            promptStep: "p3_2",
            screen: "Step2Form",
            titleKey: "p3_demo.workflow.steps.step2form.title",
            fields: [
                {
                    fieldName: "email",
                    fieldType: "email",
                    required: true,
                    label: "Email Address",
                    labelKey: "p3_demo.step2form.email.label",
                    placeholderKey: "p3_demo.step2form.email.placeholder",
                    validationRules: {
                        pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
                        patternErrorKey: "p3_demo.step2form.email.patternError"
                    },
                    autoComplete: "email"
                },
                {
                    fieldName: "phoneNumber",
                    fieldType: "tel",
                    required: true,
                    label: "Phone Number",
                    labelKey: "p3_demo.step2form.phoneNumber.label",
                    placeholderKey: "p3_demo.step2form.phoneNumber.placeholder",
                    helpTextKey: "p3_demo.step2form.phoneNumber.help",
                    validationRules: {
                        pattern: "^\\+?[0-9\\s-]{10,15}$",
                        patternErrorKey: "p3_demo.step2form.phoneNumber.patternError"
                    },
                    autoComplete: "tel"
                },
                {
                    fieldName: "comments",
                    fieldType: "textarea",
                    required: false,
                    label: "Additional Comments",
                    labelKey: "p3_demo.step2form.comments.label",
                    placeholderKey: "p3_demo.step2form.comments.placeholder",
                    rows: 4,
                    validationRules: {
                        maxLength: 500
                    }
                },
                {
                    fieldName: "preferredContact",
                    fieldType: "radio",
                    required: true,
                    label: "Preferred Contact Method",
                    labelKey: "p3_demo.step2form.preferredContact.label",
                    optionsKeys: {
                        email: "p3_demo.step2form.preferredContact.options.email",
                        phone: "p3_demo.step2form.preferredContact.options.phone",
                        sms: "p3_demo.step2form.preferredContact.options.sms"
                    },
                    defaultValue: "email"
                },
                {
                    fieldName: "supportingDocuments",
                    fieldType: "file",
                    required: false,
                    label: "Supporting Documents",
                    labelKey: "p3_demo.step2form.documents.label",
                    descriptionKey: "p3_demo.step2form.documents.description",
                    accept: "application/pdf,image/jpeg,image/png",
                    multiple: true
                },
                {
                    fieldName: "receiveUpdates",
                    fieldType: "toggle",
                    required: false,
                    label: "Receive Status Updates",
                    labelKey: "p3_demo.step2form.receiveUpdates.label",
                    defaultValue: true
                },
                {
                    fieldName: "scheduleTime",
                    fieldType: "time",
                    required: false,
                    label: "Preferred Contact Time",
                    labelKey: "p3_demo.step2form.scheduleTime.label",
                    dependsOn: {
                        field: "preferredContact",
                        value: "phone",
                        condition: "equals"
                    }
                },
                {
                    fieldName: "identificationColor",
                    fieldType: "color",
                    required: false,
                    label: "Identification Color",
                    labelKey: "p3_demo.step2form.identificationColor.label",
                    descriptionKey: "p3_demo.step2form.identificationColor.description"
                },
                {
                    fieldName: "satisfactionLevel",
                    fieldType: "range",
                    required: false,
                    label: "Satisfaction Level",
                    labelKey: "p3_demo.step2form.satisfactionLevel.label",
                    validationRules: {
                        min: 1,
                        max: 10,
                        step: 1
                    }
                }
            ],
            hasChat: true
        },
        // Add step 3
        {
            id: 3,
            key: "Step3",
            promptStep: "p3_3",
            screen: "Step3Form",
            titleKey: "p3_demo.workflow.steps.step3form.title",
            fields: [
                {
                    fieldName: "paymentMethod",
                    fieldType: "select",
                    required: true,
                    label: "Payment Method",
                    labelKey: "p3_demo.step3form.paymentMethod.label"
                },
                {
                    fieldName: "insuranceInfo",
                    fieldType: "text",
                    required: true,
                    label: "Insurance Information",
                    labelKey: "p3_demo.step3form.insuranceInfo.label"
                },
                {
                    fieldName: "odometerReading",
                    fieldType: "text",
                    required: true,
                    label: "Odometer Reading",
                    labelKey: "p3_demo.step3form.odometerReading.label"
                },
                {
                    fieldName: "transferDate",
                    fieldType: "date",
                    required: true,
                    label: "Transfer Date",
                    labelKey: "p3_demo.step3form.transferDate.label"
                },
                {
                    fieldName: "feeAcknowledged",
                    fieldType: "checkbox",
                    required: true,
                    label: "I acknowledge the transfer fee",
                    labelKey: "p3_demo.step3form.feeAcknowledgment"
                }
            ],
            hasChat: true
        },
        // Add step 4
        {
            id: 4,
            key: "Step4",
            promptStep: "p3_4",
            screen: "Step4Form",
            titleKey: "p3_demo.workflow.steps.step4form.title",
            fields: [
                {
                    fieldName: "confirmationNumber",
                    fieldType: "text",
                    required: false,
                    label: "Confirmation Number",
                    labelKey: "p3_demo.step4form.confirmationNumber.label"
                },
                {
                    fieldName: "termsAccepted",
                    fieldType: "checkbox",
                    required: true,
                    label: "Terms and Conditions",
                    labelKey: "p3_demo.step4form.termsAndConditions"
                },
                {
                    fieldName: "declared",
                    fieldType: "checkbox",
                    required: true,
                    label: "Declaration",
                    labelKey: "p3_demo.step4form.declaration"
                }
            ],
            hasChat: true
        }
    ],
    defaultAssistant: {
        tab: "chat",
        language: "en",
        lastPrompt: "p3_1",
        lastToolUsed: ""
    }
};

// Map route paths to their configs
export const DEMO_CONFIG_MAP: Record<string, DemoSiteConfig> = {
    "/basedemo": BASE_DEMO_CONFIG,
    "/p1demo": P1_DEMO_CONFIG,
    "/p2demo": P2_DEMO_CONFIG,
    "/p3demo": P3_DEMO_CONFIG
};

// Helper function to get current demo config based on pathname
export function getCurrentDemoConfig(): DemoSiteConfig {
    const pathname = window.location.pathname;
    return DEMO_CONFIG_MAP[pathname] || BASE_DEMO_CONFIG;
}

// Find which step contains a specific field
export function getStepWithField(demoConfig: DemoSiteConfig, fieldName: string): number | null {
    for (const step of demoConfig.steps) {
        if (step.fields.some(field => field.fieldName === fieldName)) {
            return step.id;
        }
    }
    return null;
}
// Add this function at the bottom of the file after the existing exports
export function createDemoSessionContext(connectionId: string, demoRoute: string): SessionContext {
    const demoConfig = DEMO_CONFIG_MAP[demoRoute] || BASE_DEMO_CONFIG;
    const baseContext = createDefaultSessionContext(connectionId);

    // Create steps structure based on config
    const steps: Record<string, any> = {};

    demoConfig.steps.forEach(step => {
        // Create fields object with empty values
        const fields: Record<string, any> = {};
        step.fields.forEach(field => {
            fields[field.fieldName] = "";
        });

        // Create step entry
        steps[step.id.toString()] = {
            screen: step.screen,
            fields: fields,
            chat: {
                history: [],
                latestTranscript: "",
                interrupts: 0
            }
        };
    });

    return {
        ...baseContext,
        layout: {
            name: demoConfig.name,
            workflow: {
                currentStep: 1,
                steps: steps
            },
            assistant: {
                ...demoConfig.defaultAssistant,
                name: demoConfig.route.includes("base") ? "BASE Assistant" : "SNSW Vehicle Transfer Assistant",
                avatarUrl: "",
                welcomeMessage: demoConfig.route.includes("base") ? "Welcome to the registration process." : "Welcome to the vehicle transfer process."
            }
        }
    };
}
